import { DEFAULT_BUY_TOKEN, DEFAULT_SELL_TOKEN } from '@src/configs/defaults'
import { IExchangeData } from '@src/types/exchange'
import { IToken } from '@src/types/token'
import { createContextLogger } from '@src/utils/logger'

const logger = createContextLogger('tokenValidation')

/**
 * Checks if two tokens are the same (same address on same chain)
 */
export function isSameToken(token1?: IToken, chainId1?: number, token2?: IToken, chainId2?: number): boolean {
    if (!token1 || !token2) return false

    const effectiveChainId1 = chainId1 ?? token1.chainId
    const effectiveChainId2 = chainId2 ?? token2.chainId

    return token1.address.toLowerCase() === token2.address.toLowerCase() && effectiveChainId1 === effectiveChainId2
}

/**
 * Checks if two exchange data objects contain the same token
 */
export function isSameExchangeToken(sellData: IExchangeData, buyData: IExchangeData): boolean {
    return isSameToken(sellData.token, sellData.chainId, buyData.token, buyData.chainId)
}

/**
 * Validates URL parameters to prevent same token on both sides
 * @returns true if parameters were valid, false if they were reset
 */
export function validateUrlTokenParams(
    params: URLSearchParams,
    updateBothData: (data: { sell: Partial<IExchangeData>; buy: Partial<IExchangeData> }) => void
): boolean {
    const sellChain = params.get('sellChain')
    const buyChain = params.get('buyChain')
    const sellToken = params.get('sellToken')
    const buyToken = params.get('buyToken')

    // Check for same token on same chain
    if (sellChain && buyChain && sellToken && buyToken) {
        if (sellChain === buyChain && sellToken.toLowerCase() === buyToken.toLowerCase()) {
            logger.info('URL parameters contain same token on same chain - resetting to defaults')

            // Reset to default tokens
            updateBothData({
                sell: {
                    token: DEFAULT_SELL_TOKEN,
                    chainId: DEFAULT_SELL_TOKEN.chainId,
                    value: BigInt(0),
                },
                buy: {
                    token: DEFAULT_BUY_TOKEN,
                    chainId: DEFAULT_BUY_TOKEN.chainId,
                    value: BigInt(0),
                },
            })

            return false
        }
    }

    return true
}

/**
 * Validates and handles token selection, preventing the same token from being selected on both sides
 */
export function handleTokenSelection(
    newData: { token?: IToken; chainId?: number },
    type: 'buy' | 'sell',
    sellData: IExchangeData,
    buyData: IExchangeData,
    updateBothData: (data: { sell: Partial<IExchangeData>; buy: Partial<IExchangeData> }) => void
): void {
    logger.info(`[handleTokenSelection] type=${type}, newData=`, newData)

    if (!newData.token) {
        logger.warn(`[handleTokenSelection]: invalid ${type} token selected:`, newData)
        return
    }

    // Calculate adjusted value based on decimal difference
    const calculateAdjustedValue = (value: bigint, fromDecimals: number, toDecimals: number): bigint => {
        if (fromDecimals === toDecimals) return value

        if (fromDecimals < toDecimals) {
            return value * BigInt(10 ** (toDecimals - fromDecimals))
        } else {
            return value / BigInt(10 ** (fromDecimals - toDecimals))
        }
    }

    if (type === 'sell') {
        const sellAdjustedValue = calculateAdjustedValue(
            sellData.value,
            sellData.token.decimals,
            newData.token.decimals
        )

        if (isSameToken(newData.token, newData.chainId, buyData.token, buyData.chainId)) {
            // If user selected DEFAULT_BUY_TOKEN, use DEFAULT_SELL_TOKEN for buy side
            // If user selected DEFAULT_SELL_TOKEN, use DEFAULT_BUY_TOKEN for buy side
            const oppositeToken = isSameToken(
                newData.token,
                newData.chainId,
                DEFAULT_BUY_TOKEN,
                DEFAULT_BUY_TOKEN.chainId
            )
                ? DEFAULT_SELL_TOKEN
                : isSameToken(newData.token, newData.chainId, DEFAULT_SELL_TOKEN, DEFAULT_SELL_TOKEN.chainId)
                  ? DEFAULT_BUY_TOKEN
                  : DEFAULT_BUY_TOKEN // fallback for non-default tokens

            updateBothData({
                sell: {
                    token: newData.token,
                    chainId: newData.token.chainId,
                    value: sellAdjustedValue,
                },
                buy: {
                    token: oppositeToken,
                    chainId: oppositeToken.chainId,
                    value: buyData.value,
                },
            })
        } else {
            // Normal update - only update sell side
            updateBothData({
                sell: {
                    token: newData.token,
                    chainId: newData.token.chainId,
                    value: sellAdjustedValue,
                },
                buy: buyData, // Keep existing buy data
            })
        }
    } else {
        const buyAdjustedValue = calculateAdjustedValue(buyData.value, buyData.token.decimals, newData.token.decimals)

        if (isSameToken(newData.token, newData.chainId, sellData.token, sellData.chainId)) {
            // If user selected DEFAULT_SELL_TOKEN, use DEFAULT_BUY_TOKEN for sell side
            // If user selected DEFAULT_BUY_TOKEN, use DEFAULT_SELL_TOKEN for sell side
            const oppositeToken = isSameToken(
                newData.token,
                newData.chainId,
                DEFAULT_SELL_TOKEN,
                DEFAULT_SELL_TOKEN.chainId
            )
                ? DEFAULT_BUY_TOKEN
                : isSameToken(newData.token, newData.chainId, DEFAULT_BUY_TOKEN, DEFAULT_BUY_TOKEN.chainId)
                  ? DEFAULT_SELL_TOKEN
                  : DEFAULT_SELL_TOKEN // fallback for non-default tokens

            updateBothData({
                buy: {
                    token: newData.token,
                    chainId: newData.token.chainId,
                    value: buyAdjustedValue,
                },
                sell: {
                    token: oppositeToken,
                    chainId: oppositeToken.chainId,
                    value: sellData.value,
                },
            })
        } else {
            // Normal update - only update buy side
            updateBothData({
                buy: {
                    token: newData.token,
                    chainId: newData.token.chainId,
                    value: buyAdjustedValue,
                },
                sell: sellData, // Keep existing sell data
            })
        }
    }
}

/**
 * Validates that default tokens don't create duplicates and handles this case
 * @returns The appropriate tokens to use that won't create duplicates
 */
export function validateDefaultTokens(
    sellData: IExchangeData,
    buyData: IExchangeData
): { sellToken: IToken; buyToken: IToken } {
    // Check if resetting to defaults would create a duplicate
    if (
        DEFAULT_SELL_TOKEN.chainId === DEFAULT_BUY_TOKEN.chainId &&
        DEFAULT_SELL_TOKEN.address.toLowerCase() === DEFAULT_BUY_TOKEN.address.toLowerCase()
    ) {
        logger.info('Default tokens would create a duplicate - providing alternative defaults')

        // If the current sell token is already the DEFAULT_SELL_TOKEN,
        // then we need to find an alternative for buy token
        if (
            sellData.token?.address.toLowerCase() === DEFAULT_SELL_TOKEN.address.toLowerCase() &&
            sellData.chainId === DEFAULT_SELL_TOKEN.chainId
        ) {
            // Return sell token as is and find alternative buy token
            // This could be a predefined alternative or the current buy token if it's different
            const alternativeBuyToken = isSameToken(
                buyData.token,
                buyData.chainId,
                DEFAULT_SELL_TOKEN,
                DEFAULT_SELL_TOKEN.chainId
            )
                ? /* define a backup default buy token */ DEFAULT_BUY_TOKEN
                : buyData.token

            return {
                sellToken: DEFAULT_SELL_TOKEN,
                buyToken: alternativeBuyToken,
            }
        } else {
            // Otherwise, keep buy as DEFAULT_BUY_TOKEN and find alternative for sell
            return {
                sellToken: /* define a backup default sell token */ DEFAULT_SELL_TOKEN,
                buyToken: DEFAULT_BUY_TOKEN,
            }
        }
    }

    // No duplicate issue, return normal defaults
    return {
        sellToken: DEFAULT_SELL_TOKEN,
        buyToken: DEFAULT_BUY_TOKEN,
    }
}
